/**
 * ProgramContext — the active PROGRAM, alongside the active organization.
 *
 * Amigos de Guadalupe run three programs out of one tenant (TRUS-548) and asked for a
 * way to choose which one they are working in. This is the org switcher's sibling:
 * same localStorage-plus-event shape as OrganizationContext, one level down.
 *
 * Three things are deliberate:
 *
 *   * "All programs" (null) is a first-class value, not a missing one. Every org that
 *     never enables the switcher sits on null forever, so null is the default path and
 *     the one that gets exercised — a consumer that breaks without a program id breaks
 *     for everyone, not just for the org that turned this on.
 *
 *   * The selection is cleared when the organization changes. Program ids are org-scoped;
 *     carrying one across a tenant switch is the same class of bug that made the
 *     per-program field override screens 404 for super admins.
 *
 *   * `enabled` is capability AND plurality. A one-program org that somehow holds the
 *     capability still gets no control, because a picker with one option is furniture.
 *
 * `localStorage['active_program_id']` already existed and was read by the Form Library —
 * it simply had no writer. This is the writer.
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { programsAPI, type Program } from '@/services/api/programs';
import { useCapability } from '@/hooks/useCapabilities';

export const ACTIVE_PROGRAM_KEY = 'active_program_id';
export const PROGRAM_CHANGED_EVENT = 'programChanged';

interface ProgramContextType {
  /** Active programs for the current org. Empty until the capability is on. */
  programs: Program[];
  /** null means "All programs" — the default, and the only value most orgs ever have. */
  selectedProgram: Program | null;
  selectedProgramId: number | null;
  /** True only when the org holds `program_switcher` AND has two or more programs. */
  enabled: boolean;
  loading: boolean;
  setSelectedProgram: (program: Program | null) => void;
  refetch: () => Promise<void>;
}

const ProgramContext = createContext<ProgramContextType>({
  programs: [],
  selectedProgram: null,
  selectedProgramId: null,
  enabled: false,
  loading: false,
  setSelectedProgram: () => {},
  refetch: async () => {},
});

function readActiveOrgId(): number | null {
  const raw = localStorage.getItem('active_cbo_id');
  const parsed = raw ? parseInt(raw, 10) : NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function ProgramProvider({ children }: { children: ReactNode }) {
  const [orgId, setOrgId] = useState<number | null>(() => readActiveOrgId());
  const { enabled: capabilityOn } = useCapability(orgId, 'program_switcher');

  const [programs, setPrograms] = useState<Program[]>([]);
  const [selectedProgram, setSelectedProgramState] = useState<Program | null>(null);
  const [loading, setLoading] = useState(false);

  // Follow the org switcher. A program from the previous tenant must not survive.
  useEffect(() => {
    const handleOrgChange = () => {
      const next = readActiveOrgId();
      setOrgId((prev) => {
        if (prev === next) return prev;
        localStorage.removeItem(ACTIVE_PROGRAM_KEY);
        setSelectedProgramState(null);
        setPrograms([]);
        return next;
      });
    };
    window.addEventListener('organizationChanged', handleOrgChange);
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'active_cbo_id') handleOrgChange();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('organizationChanged', handleOrgChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const fetchPrograms = useCallback(async () => {
    if (!capabilityOn || !orgId) {
      setPrograms([]);
      return;
    }
    try {
      setLoading(true);
      const rows = await programsAPI.listPrograms({ is_active: true });
      // listPrograms returns programs across ALL orgs for a super admin. Filtering to
      // the active tenant here is what keeps a super admin from being offered — and
      // then 404'd by — another org's program.
      const mine = (rows || []).filter((p) => p.cbo_id === orgId);
      setPrograms(mine);

      const storedRaw = localStorage.getItem(ACTIVE_PROGRAM_KEY);
      const stored = storedRaw ? parseInt(storedRaw, 10) : NaN;
      const match = Number.isFinite(stored) ? mine.find((p) => p.id === stored) : undefined;
      if (match) {
        setSelectedProgramState(match);
      } else {
        // A stale id from a deleted or re-scoped program falls back to "All programs"
        // rather than filtering every screen by something that no longer exists.
        if (storedRaw) localStorage.removeItem(ACTIVE_PROGRAM_KEY);
        setSelectedProgramState(null);
      }
    } catch {
      // A program list that will not load must not break the shell it renders in.
      setPrograms([]);
      setSelectedProgramState(null);
    } finally {
      setLoading(false);
    }
  }, [capabilityOn, orgId]);

  useEffect(() => {
    void fetchPrograms();
  }, [fetchPrograms]);

  const setSelectedProgram = useCallback((program: Program | null) => {
    setSelectedProgramState(program);
    if (program) {
      localStorage.setItem(ACTIVE_PROGRAM_KEY, String(program.id));
    } else {
      localStorage.removeItem(ACTIVE_PROGRAM_KEY);
    }
    window.dispatchEvent(new Event(PROGRAM_CHANGED_EVENT));

    // Land the user in this program's home, when it has one. The switcher changes
    // WHERE YOU LAND and never what you can reach: no page is hidden or removed by
    // choosing a program, because staff overlap across programs and taking a page
    // away would take away somebody's own work. A program with no landing tab — every
    // program today — leaves the user exactly where they were.
    const landing = program?.default_nav_tab;
    if (landing) {
      try {
        localStorage.setItem('activeTab', landing);
      } catch {
        /* private mode — the event below still moves the shell */
      }
      window.dispatchEvent(new CustomEvent('changeTab', { detail: landing }));
    }
  }, []);

  const enabled = capabilityOn && programs.length >= 2;

  return (
    <ProgramContext.Provider
      value={{
        programs,
        // With the switcher off there is no active program, whatever is in storage.
        selectedProgram: enabled ? selectedProgram : null,
        selectedProgramId: enabled ? (selectedProgram?.id ?? null) : null,
        enabled,
        loading,
        setSelectedProgram,
        refetch: fetchPrograms,
      }}
    >
      {children}
    </ProgramContext.Provider>
  );
}

export function useProgram(): ProgramContextType {
  return useContext(ProgramContext);
}
